import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PendingApprovalsProvider } from "@/context/PendingApprovalsContext";
import { Toaster } from "sonner";

import Login from "@/pages/Login";
import AppShell from "@/components/layout/AppShell";
import Dashboard from "@/pages/Dashboard";
import Residents from "@/pages/Residents";
import Fees from "@/pages/Fees";
import ResidenceFees from "@/pages/ResidenceFees";
import MessFees from "@/pages/MessFees";
import AIAssistant from "@/pages/AIAssistant";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import BedAllocation from "@/pages/BedAllocation";
import Expenses from "@/pages/Expenses";
import Assets from "@/pages/Assets";
import Inventory from "@/pages/Inventory";
import Guest from "@/pages/Guest";
import Reports from "@/pages/Reports";
import PettyCash from "@/pages/PettyCash";
import StudentLedger from "@/pages/StudentLedger";
import Users from "@/pages/Users";
import AuditLog from "@/pages/AuditLog";
import ForcePasswordChange from "@/pages/ForcePasswordChange";
import ExpiringResidents from "@/pages/ExpiringResidents";
import OutstandingDues from "@/pages/OutstandingDues";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Loading...
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <ForcePasswordChange />;
  return children;
}

function Public({ children }) {
  const { user } = useAuth();
  if (user === null) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <PendingApprovalsProvider>
          <BrowserRouter>
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/login" element={<Public><Login /></Public>} />
            <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
            <Route path="/payment/cancel" element={<Protected><PaymentCancel /></Protected>} />
            <Route element={<Protected><AppShell /></Protected>}>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/residents" element={<Residents />} />
              <Route path="/residents/expiring" element={<ExpiringResidents />} />
              <Route path="/residents/:rid/ledger" element={<StudentLedger />} />
              <Route path="/beds" element={<BedAllocation />} />
              <Route path="/fees" element={<Fees />} />
              <Route path="/outstanding" element={<OutstandingDues />} />
              <Route path="/residence-fees" element={<ResidenceFees />} />
              <Route path="/mess-fees" element={<MessFees />} />
              <Route path="/expenses" element={<Expenses />} />
              <Route path="/assets" element={<Assets />} />
              <Route path="/inventory" element={<Inventory />} />
              <Route path="/guests" element={<Guest />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/petty-cash" element={<PettyCash />} />
              <Route path="/ai" element={<AIAssistant />} />
              <Route path="/users" element={<Users />} />
              <Route path="/audit" element={<AuditLog />} />
            </Route>
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
        </PendingApprovalsProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
